import {
    createPushNotificationClientReadyMessage,
    parseNativePushNotificationDetail,
    parsePushNotificationServiceWorkerMessage,
    type PushNotificationEvent
} from './events.js'
import { getNativeMessageHandler } from '../../runtime/native-bridge.js'

export type PushPermissionState =
    | 'authorized'
    | 'default'
    | 'denied'
    | 'ephemeral'
    | 'granted'
    | 'notDetermined'
    | 'provisional'
    | 'unavailable'
    | 'unknown'

export type PushNotificationListener = (event: PushNotificationEvent) => void

const NativeNotificationEventName = 'push-notification'
const NativePermissionRequestEventName = 'push-permission-request'
const NativePermissionStateEventName = 'push-permission-state'
const NativePermissionRequestHandlerName = 'push-permission-request'
const NativePermissionStateHandlerName = 'push-permission-state'
const NativeBridgeTimeoutMs = 5000

export class PushNotificationsService {
    readonly #listeners = new Set<PushNotificationListener>()
    readonly #pendingClickedEvents: PushNotificationEvent[] = []
    #started = false

    public start(): void {
        if (this.#started || typeof window === 'undefined') {
            return
        }

        window.addEventListener(NativeNotificationEventName, this.#handleNativeNotification)
        PushNotificationsService.#getServiceWorkerContainer()?.addEventListener(
            'message',
            this.#handleServiceWorkerMessage
        )
        PushNotificationsService.#getServiceWorkerController()?.postMessage(
            createPushNotificationClientReadyMessage()
        )
        this.#started = true
    }

    public destroy(): void {
        if (!this.#started || typeof window === 'undefined') {
            return
        }

        window.removeEventListener(NativeNotificationEventName, this.#handleNativeNotification)
        PushNotificationsService.#getServiceWorkerContainer()?.removeEventListener(
            'message',
            this.#handleServiceWorkerMessage
        )
        this.#listeners.clear()
        this.#started = false
    }

    public subscribe(listener: PushNotificationListener): () => void {
        this.#listeners.add(listener)
        this.#drainPendingClickedEvents(listener)

        return () => {
            this.#listeners.delete(listener)
        }
    }

    public async getPermissionState(): Promise<PushPermissionState> {
        if (this.#hasNativePermissionBridge()) {
            return await this.#requestNativePermissionState()
        }

        return this.#getWebPermissionState()
    }

    public async requestPermission(): Promise<PushPermissionState> {
        const currentState = await this.getPermissionState()

        if (
            PushNotificationsService.#isPermissionAllowed(currentState) ||
            currentState === 'denied'
        ) {
            return currentState
        }

        if (this.#hasNativePermissionBridge()) {
            return await this.#requestNativePermission()
        }

        if (!this.#hasWebNotificationApi()) {
            return 'unavailable'
        }

        return await Notification.requestPermission()
    }

    #handleNativeNotification = (event: Event): void => {
        const detail = PushNotificationsService.#getEventDetail(event)
        const notification = parseNativePushNotificationDetail(detail)

        if (!notification) {
            return
        }

        this.#emit({
            ...notification,
            source: 'native'
        })
    }

    #handleServiceWorkerMessage = (event: MessageEvent): void => {
        const notification = parsePushNotificationServiceWorkerMessage(event.data)

        if (notification) {
            this.#emit(notification)
        }
    }

    #emit(event: PushNotificationEvent): void {
        if (this.#listeners.size === 0 && event.interaction === 'clicked') {
            this.#pendingClickedEvents.push(event)
            return
        }

        for (const listener of this.#listeners) {
            listener(event)
        }
    }

    #drainPendingClickedEvents(listener: PushNotificationListener): void {
        const pendingEvents = this.#pendingClickedEvents.splice(0)

        for (const event of pendingEvents) {
            listener(event)
        }
    }

    #hasNativePermissionBridge(): boolean {
        return Boolean(
            getNativeMessageHandler(NativePermissionRequestHandlerName) &&
                getNativeMessageHandler(NativePermissionStateHandlerName)
        )
    }

    async #requestNativePermissionState(): Promise<PushPermissionState> {
        const statePromise = PushNotificationsService.#waitForPermissionEvent(
            NativePermissionStateEventName
        )
        getNativeMessageHandler(NativePermissionStateHandlerName)?.postMessage(null)
        return await statePromise
    }

    async #requestNativePermission(): Promise<PushPermissionState> {
        const statePromise = PushNotificationsService.#waitForPermissionEvent(
            NativePermissionRequestEventName
        )
        getNativeMessageHandler(NativePermissionRequestHandlerName)?.postMessage(null)
        return await statePromise
    }

    #getWebPermissionState(): PushPermissionState {
        if (!this.#hasWebNotificationApi()) {
            return 'unavailable'
        }

        return Notification.permission
    }

    #hasWebNotificationApi(): boolean {
        return typeof Notification !== 'undefined' && typeof Notification.requestPermission === 'function'
    }

    static #getEventDetail(event: Event): unknown {
        if ('detail' in event) {
            return event.detail
        }

        return undefined
    }

    static #getServiceWorkerContainer(): ServiceWorkerContainer | undefined {
        if (typeof navigator === 'undefined') {
            return undefined
        }

        return navigator.serviceWorker
    }

    static #getServiceWorkerController(): ServiceWorker | null {
        return PushNotificationsService.#getServiceWorkerContainer()?.controller ?? null
    }

    static #isPermissionAllowed(state: PushPermissionState): boolean {
        return (
            state === 'authorized' ||
            state === 'ephemeral' ||
            state === 'granted' ||
            state === 'provisional'
        )
    }

    static #normalizePermissionState(value: unknown): PushPermissionState {
        if (
            value === 'authorized' ||
            value === 'default' ||
            value === 'denied' ||
            value === 'ephemeral' ||
            value === 'granted' ||
            value === 'notDetermined' ||
            value === 'provisional' ||
            value === 'unavailable' ||
            value === 'unknown'
        ) {
            return value
        }

        return 'unknown'
    }

    static #waitForPermissionEvent(eventName: string): Promise<PushPermissionState> {
        if (typeof window === 'undefined') {
            return Promise.resolve('unavailable')
        }

        return new Promise((resolve) => {
            let resolved = false
            const timeout = window.setTimeout(() => {
                finish('unavailable')
            }, NativeBridgeTimeoutMs)

            const finish = (state: PushPermissionState): void => {
                if (resolved) {
                    return
                }

                resolved = true
                window.clearTimeout(timeout)
                window.removeEventListener(eventName, handleEvent)
                resolve(state)
            }

            const handleEvent = (event: Event): void => {
                finish(
                    PushNotificationsService.#normalizePermissionState(
                        PushNotificationsService.#getEventDetail(event)
                    )
                )
            }

            window.addEventListener(eventName, handleEvent)
        })
    }
}
